/**
 * FeatureErrorBoundary Component
 *
 * Inline error boundary for feature-level error handling.
 * Captures errors to Sentry when configured and shows a clean inline UI.
 */

import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';
import * as Sentry from '@sentry/react';
import { errorLogger } from '@/lib/errorLogger';
import { breadcrumbs } from '@/lib/breadcrumbs';
import { isSentryConfigured } from '@/lib/sentry';
import { ErrorFallback } from './ErrorFallback';

interface FeatureErrorBoundaryProps {
  children: ReactNode;
  featureName: string;
  fallbackTitle?: string;
  fallbackMessage?: string;
  onRetry?: () => void;
  onClose?: () => void;
  closeLabel?: string;
  showToast?: boolean;
}

interface FeatureErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorId?: string;
  errorTimestamp?: string;
  sentryReported: boolean;
}

export class FeatureErrorBoundary extends Component<
  FeatureErrorBoundaryProps,
  FeatureErrorBoundaryState
> {
  constructor(props: FeatureErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      sentryReported: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<FeatureErrorBoundaryState> {
    return {
      hasError: true,
      error,
      errorId: `feature_error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      errorTimestamp: new Date().toISOString(),
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { featureName } = this.props;
    const { errorId } = this.state;

    // Log to local error logger
    errorLogger.logError(error, errorInfo, {
      category: 'react',
      metadata: {
        featureName,
        featureType: 'feature_boundary',
      },
      retryable: true,
    });

    // Track in breadcrumbs
    breadcrumbs.error(`[${featureName}] ${error.message}`, {
      errorId,
    });

    // Send to Sentry if configured
    if (isSentryConfigured()) {
      const sentryClient = Sentry.getClient();
      if (sentryClient) {
        Sentry.captureException(error, {
          tags: {
            source: 'feature-error-boundary',
            featureName,
          },
          extra: {
            errorId,
            componentStack: errorInfo.componentStack,
          },
        });
        this.setState({ sentryReported: true });
      }
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: undefined,
      errorId: undefined,
      errorTimestamp: undefined,
      sentryReported: false,
    });
    this.props.onRetry?.();
  };

  render() {
    if (this.state.hasError) {
      const { featureName, fallbackTitle, fallbackMessage, onClose, closeLabel } = this.props;
      const { error, errorId, errorTimestamp, sentryReported } = this.state;

      return (
        <ErrorFallback
          variant="inline"
          title={fallbackTitle || `Errore in ${featureName}`}
          message={fallbackMessage || 'Si è verificato un errore durante il caricamento. Riprova.'}
          errorId={errorId}
          errorTimestamp={errorTimestamp}
          error={error}
          onRetry={this.handleRetry}
          onClose={onClose}
          closeLabel={closeLabel}
          sentryReported={sentryReported}
        />
      );
    }

    return this.props.children;
  }
}
